import * as Comlink from 'comlink';
import type { McWorkerApi } from './mc.worker';
import type { ReportWorkerApi } from './report.worker';
import type { SimWorkerApi } from './sim.worker';

export interface SimWorker {
  api: Comlink.Remote<SimWorkerApi>;
  terminate: () => void;
}

/** Spawns the deterministic simulation worker (lazily, from the simulation model). */
export function createSimWorker(): SimWorker {
  const worker = new Worker(new URL('./sim.worker.ts', import.meta.url), {
    type: 'module',
    name: 'finsim-sim',
  });
  return {
    api: Comlink.wrap<SimWorkerApi>(worker),
    terminate: () => {
      worker.terminate();
    },
  };
}

export interface McWorker {
  api: Comlink.Remote<McWorkerApi>;
  terminate: () => void;
}

/** Spawns a Monte Carlo worker (one per run; terminated to cancel). */
export function createMcWorker(): McWorker {
  const worker = new Worker(new URL('./mc.worker.ts', import.meta.url), {
    type: 'module',
    name: 'finsim-mc',
  });
  return {
    api: Comlink.wrap<McWorkerApi>(worker),
    terminate: () => {
      worker.terminate();
    },
  };
}

export interface ReportWorker {
  api: Comlink.Remote<ReportWorkerApi>;
  terminate: () => void;
}

/** Spawns the PDF report worker (one per export; terminated to cancel). */
export function createReportWorker(): ReportWorker {
  const worker = new Worker(new URL('./report.worker.ts', import.meta.url), {
    type: 'module',
    name: 'finsim-report',
  });
  return {
    api: Comlink.wrap<ReportWorkerApi>(worker),
    terminate: () => {
      worker.terminate();
    },
  };
}

/** Wraps a callback so the worker can call it across the thread boundary. */
export const proxyCallback = <T extends (...args: never[]) => unknown>(fn: T): T =>
  Comlink.proxy(fn);
